import { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { serviceColor } from '../utils/colors';

interface ServiceNodesProps {
  services: string[];
  timelineRadius: number;
  onSelectService: (service: string | null) => void;
  activeService: string | null;
}

export const servicePositionFromIndex = (
  index: number,
  total: number,
  radius: number
): THREE.Vector3Tuple => {
  if (total <= 0) {
    return [0, 0.2, 0];
  }

  const angle = (index / total) * Math.PI * 2;
  const wobble = Math.sin(index * 1.7) * 0.4;
  return [Math.cos(angle) * radius, wobble, Math.sin(angle) * radius];
};

export default function ServiceNodes({
  services,
  timelineRadius,
  onSelectService,
  activeService
}: ServiceNodesProps) {
  const count = Math.max(services.length, 1);
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || services.length === 0) {
      return;
    }

    services.forEach((service, index) => {
      const [x, y, z] = servicePositionFromIndex(index, services.length, timelineRadius);
      dummy.position.set(x, y, z);
      const highlighted = !activeService || activeService === service;
      const baseScale = highlighted ? 1 : 0.82;
      dummy.scale.set(baseScale, 0.6 + ((index % 4) + 1) * 0.15, baseScale);
      dummy.lookAt(0, y, 0);
      dummy.updateMatrix();

      mesh.setMatrixAt(index, dummy.matrix);
      const color = serviceColor(service).multiplyScalar(highlighted ? 1.1 : 0.4);
      mesh.setColorAt(index, color);
    });

    mesh.count = services.length;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) {
      mesh.instanceColor.needsUpdate = true;
    }
  }, [activeService, dummy, services, timelineRadius]);

  return (
    <group>
      <instancedMesh
        ref={meshRef}
        args={[undefined, undefined, count]}
        onClick={(event) => {
          if (typeof event.instanceId !== 'number') {
            return;
          }

          const selectedService = services[event.instanceId] ?? null;
          if (selectedService && selectedService === activeService) {
            onSelectService(null);
            return;
          }

          onSelectService(selectedService);
        }}
      >
        <cylinderGeometry args={[0.35, 0.65, 1.2, 24, 1, false]} />
        <meshBasicMaterial transparent opacity={0.92} toneMapped={false} />
      </instancedMesh>

      {services.map((service, index) => {
        const [x, y, z] = servicePositionFromIndex(index, services.length, timelineRadius);
        return (
          <Text
            key={service}
            position={[x, y + 1.2, z]}
            maxWidth={3}
            fontSize={0.25}
            color={serviceColor(service)}
            anchorX="center"
            anchorY="middle"
            fontWeight={700}
            outlineWidth={0.02}
            outlineColor="#041329"
          >
            {service}
          </Text>
        );
      })}
    </group>
  );
}
